import { type InputHTMLAttributes, type Ref, type TextareaHTMLAttributes, useId } from 'react';

import styles from './TextField.module.css';

interface FieldShellProps {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
}

type TextFieldProps = FieldShellProps &
  InputHTMLAttributes<HTMLInputElement> & { ref?: Ref<HTMLInputElement> };

/** Labelled input with an accessible error message (`aria-describedby`). */
export function TextField({ label, error, hint, id, ...props }: TextFieldProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const describedBy = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined;
  return (
    <div className={styles.field}>
      <label htmlFor={inputId} className={styles.label}>
        {label}
      </label>
      <input
        id={inputId}
        className={styles.input}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        // a dialog focuses this field once it is open (React's autoFocus runs before that)
        data-autofocus={props.autoFocus ? 'true' : undefined}
        {...props}
      />
      {error ? (
        <span id={`${inputId}-error`} className={styles.error} role="alert">
          {error}
        </span>
      ) : (
        hint && (
          <span id={`${inputId}-hint`} className={styles.hint}>
            {hint}
          </span>
        )
      )}
    </div>
  );
}

type TextAreaFieldProps = FieldShellProps &
  TextareaHTMLAttributes<HTMLTextAreaElement> & { ref?: Ref<HTMLTextAreaElement> };

export function TextAreaField({ label, error, hint, id, ...props }: TextAreaFieldProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  return (
    <div className={styles.field}>
      <label htmlFor={inputId} className={styles.label}>
        {label}
      </label>
      <textarea
        id={inputId}
        className={`${styles.input} ${styles.textarea}`}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${inputId}-error` : undefined}
        data-autofocus={props.autoFocus ? 'true' : undefined}
        {...props}
      />
      {error && (
        <span id={`${inputId}-error`} className={styles.error} role="alert">
          {error}
        </span>
      )}
      {!error && hint && <span className={styles.hint}>{hint}</span>}
    </div>
  );
}
